import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { Button, Details, FirstItem, LastTableItem, TableBodyRow } from './OrdersPage.styles.js';

import { partnerOrderState } from '../../utils/constants.js';
import { formatDate, priceFormatter } from '../../utils/utils.js';
import openToast from '../../utils/toast.js';

import MainContainer from '../../components/utils/MainContainer/MainContainer.js';
import TableItem from '../../components/Table/TableItem.js';
import Table from '../../components/Table/Table.js';
import { Loading } from '../../components/MiniLoader/MiniLoader.styles.js';

import { selectFetchOrdersPending, selectOrders, selectSetOrderStatePending } from '../../store/slices/order/slice.js';
import { fetchOrders, setOrderState } from '../../store/slices/order/asyncThunks.js';

const tableHeader = [
  {
    label: 'order-no',
    sortable: true,
    key: 'orderNo',
  },
  {
    label: 'date',
    sortable: true,
    key: 'date',
  },
  {
    label: 'amount',
    sortable: true,
    key: 'amount',
  },
  {
    label: 'status',
    sortable: true,
    key: 'status',
  },
  {
    label: 'last-update',
    sortable: true,
    key: 'lastUpdate',
  },
  {
    label: 'partner',
    sortable: true,
    key: 'partner',
  },
  {
    label: 'actions',
    sortable: false,
    key: 'actions',
  },
];

const OrdersPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toastId = useRef(null);

  const { data, maxReached, page, total, perPage } = useSelector(selectOrders);
  const fetchOrdersPending = useSelector(selectFetchOrdersPending);
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const pageParam = queryParams.get('page');

  const [searchTerm, setSearchTerm] = useState('');
  const [editingOrderId, setEditingOrderId] = useState(null);
  const setOrderStatePending = useSelector(selectSetOrderStatePending);

  useEffect(() => {
    dispatch(fetchOrders(pageParam))
      .unwrap()
      .then(() => {})
      .catch(error => {
        // toast.error(t(error.message));
      });
  }, [dispatch, pageParam]);

  const handlePageChange = pageNumber => {
    const query = { page: pageNumber };
    const queryString = new URLSearchParams(query).toString();
    const url = `/orders?${queryString}`;

    navigate(url);
  };

  const handleState = ({ id, state }) => {
    setEditingOrderId(id);
    dispatch(setOrderState({ id, data: { state } }))
      .unwrap()
      .then(() => {
        setEditingOrderId(null);
        openToast(toastId, 'state-success', 'success', t('order-state-has-been-changed-successfully'));
        navigate(`/order-details/${id}`);
      })
      .catch(error => {
        openToast(toastId, 'error-create', 'error', error.message);
      });
  };

  return (
    <MainContainer pageTitle={'orders'}>
      <Table
        key={searchTerm}
        headerInformation={tableHeader}
        totalCount={total}
        currentPage={+pageParam}
        setPage={handlePageChange}
        perPage={perPage}
        noDataContent={'no-data'}
        lastColumnAligned
        maxReached={maxReached}
        dataLength={data.length}
        dataLoading={fetchOrdersPending}
        scroll={true}
        centerText>
        {data.map((order, key) => {
          return (
            <TableBodyRow key={key} onClick={() => navigate(`/order-details/${order.id}`)}>
              <FirstItem showHover data={order?.id} center />
              <TableItem data={formatDate(order?.createdAt) || '/'} center />
              <TableItem data={`${priceFormatter(order?.totalPrice)} SR` || '/'} center />
              <TableItem data={order?.state ? t(`${order?.state}`) : '/'} center />
              <TableItem data={formatDate(order?.updatedAt) || '/'} center />
              <TableItem data={order?.partnerUser ? order?.partnerUser.fullName : '/'} center />
              <LastTableItem
                $offClick
                data={
                  order?.state === 'accepted' ? (
                    <Button
                      onClick={() => {
                        handleState({ id: order.id, state: partnerOrderState.readyForPickup });
                      }}>
                      {editingOrderId === order.id && setOrderStatePending ? <Loading height={20} /> : t(`${'readyForPickup'}`)}
                    </Button>
                  ) : order?.state === 'created' ? (
                    <Button
                      $accepted
                      onClick={() => {
                        handleState({ id: order.id, state: partnerOrderState.accepted });
                      }}>
                      {editingOrderId === order.id && setOrderStatePending ? <Loading height={20} /> : t('accept')}
                    </Button>
                  ) : null
                }
              />
            </TableBodyRow>
          );
        })}
      </Table>
    </MainContainer>
  );
};

export default OrdersPage;
